import React, { useState } from 'react';

// Components
import Modal from '../Modal/Modal';
import InteractiveButton from '../InteractiveButton/InteractiveButton';

// Types
import { ElementSize } from '../../types/element-size';

export type MissingCalCertController = {
  id: string;
  name: string;
};

type ComponentProps = {
  isVisible: boolean;
  controllers: MissingCalCertController[];
  onClose: () => void;
  onDisableCheck: (controllerId: string) => void | Promise<void>;
  onProceedWithoutCalCert: () => void | Promise<void>;
  proceedButtonText?: string;
};

const CalCertWarningModal: React.FC<ComponentProps> = ({
  isVisible,
  controllers,
  onClose,
  onDisableCheck,
  onProceedWithoutCalCert,
  proceedButtonText = 'Proceed without Cal-Cert',
}) => {
  const [disablingControllerId, setDisablingControllerId] = useState<string | null>(null);
  const [isProceeding, setIsProceeding] = useState<boolean>(false);

  const handleDisableCheck = async (controllerId: string) => {
    setDisablingControllerId(controllerId);
    try {
      await onDisableCheck(controllerId);
    } finally {
      setDisablingControllerId(null);
    }
  };

  const handleProceedWithoutCalCert = async () => {
    setIsProceeding(true);
    try {
      await onProceedWithoutCalCert();
    } finally {
      setIsProceeding(false);
    }
  };

  return (
    <Modal
      modalTitle="Missing Calibration Certificate"
      isVisible={isVisible}
      onClose={onClose}
      onAccept={handleProceedWithoutCalCert}
      showAcceptBtn
      showCancelBtn
      cancelBtnText="Cancel"
      acceptBtnText={isProceeding ? 'Proceeding...' : proceedButtonText}
      cancelBtnDisabled={isProceeding}
      acceptBtnDisabled={isProceeding || Boolean(disablingControllerId)}
    >
      <div className="flex flex-col gap-4 text-left">
        <p className="text-sm text-slate-gray dark:text-white">
          The following controllers do not have a valid calibration certificate:
        </p>
        <ul className="flex flex-col gap-2">
          {controllers.map((controller) => (
            <li
              key={controller.id}
              className="flex items-center justify-between gap-3 rounded-sm border border-stroke p-3 dark:border-strokedark"
            >
              <span className="font-medium text-black dark:text-white">{controller.name}</span>
              <InteractiveButton
                size={ElementSize.XS}
                text={disablingControllerId === controller.id ? 'Disabling...' : 'Disable check for this controller'}
                disabled={Boolean(disablingControllerId) || isProceeding}
                onClick={() => handleDisableCheck(controller.id)}
              />
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
};

export default CalCertWarningModal;
